// Centralized response handling for PULSE pages (Activity Monitor, OBD Sign-On).
//
// Every fetch that can fail in front of a user goes through pulseRequest so:
//  - a 401 ANYWHERE funnels into the page's full-screen "sign in again" state
//    (the stored token is cleared here; callers check `sessionExpired`),
//  - raw server JSON never reaches a snackbar — non-401 errors always carry
//    human text in `message` (the server's own message for 4xx when it has
//    one, fixed wording for 5xx and network failures).
//
// Never throws. Always resolves to:
//   { ok, status, data, message, sessionExpired }
// status 0 = network failure (server unreachable / no connection).

export async function pulseRequest(url, options = {}) {
  let res;
  try {
    res = await fetch(url, options);
  } catch (err) {
    return {
      ok: false,
      status: 0,
      data: null,
      sessionExpired: false,
      message: "Can't reach PULSE. Check the connection and try again.",
    };
  }

  let data = null;
  try {
    data = await res.json();
  } catch (err) {
    // Non-JSON body (proxy error page, empty body) — never surface it.
    data = null;
  }

  if (res.status === 401) {
    // Session expired/invalid: clear the stored token so the re-login flow
    // starts clean. Callers flip their full-screen re-login state on this.
    localStorage.removeItem('authToken');
    return {
      ok: false,
      status: 401,
      data,
      sessionExpired: true,
      message: 'The login session has expired. Please sign in again.',
    };
  }

  if (res.ok) {
    return { ok: true, status: res.status, data, sessionExpired: false, message: null };
  }

  if (res.status >= 500) {
    return {
      ok: false,
      status: res.status,
      data,
      sessionExpired: false,
      message: 'PULSE is having trouble. Try again in a moment.',
    };
  }

  // Other 4xx: prefer the server's human message (e.g. "Ask a lead to pause
  // this OBD"), never the JSON envelope.
  const serverMessage =
    (data && typeof data.error === 'string' && data.error) ||
    (data && typeof data.message === 'string' && data.message) ||
    null;
  return {
    ok: false,
    status: res.status,
    data,
    sessionExpired: false,
    message: serverMessage || 'Something went wrong. Please try again.',
  };
}
